import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import honeywellLogo from '../honeywell-logo.png';
import api from '../services/api';
import ShopLayout from '../components/shop/ShopLayout';
import ProductCard from '../components/shop/ProductCard';
import CategoryProductRow from '../components/shop/CategoryProductRow';
import Pagination from '../components/shop/Pagination';
import { mapApiProductToCard, getRecentSearches } from '../utils/shopHelpers';
import './CategoryPage.css';
import './CategoryPageModern.css';
import './SearchPage.css';
import Seo from '../components/seo/Seo';
import { PAGE_SEO, truncate } from '../config/seo';

const PRICE_RANGES = [
  { label: 'Under KES 50,000', min: '', max: '50000' },
  { label: 'KES 50,000 – 100,000', min: '50000', max: '100000' },
  { label: 'KES 100,000 – 200,000', min: '100000', max: '200000' },
  { label: 'KES 200,000+', min: '200000', max: '' }
];

function SearchPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const recentSearches = getRecentSearches();

  const [viewMode, setViewMode] = useState('list');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('featured');
  const [products, setProducts] = useState([]);
  const [brandFilters, setBrandFilters] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedPrice, setSelectedPrice] = useState('');
  const [stockFilter, setStockFilter] = useState('');

  const priceRange = useMemo(
    () => PRICE_RANGES.find((r) => r.label === selectedPrice),
    [selectedPrice]
  );

  useEffect(() => {
    setPage(1);
    setSelectedBrand('');
    setSelectedPrice('');
    setStockFilter('');
    setSortBy('featured');
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [selectedBrand, selectedPrice, stockFilter, sortBy, itemsPerPage]);

  useEffect(() => {
    if (!query.trim()) {
      setProducts([]);
      setBrandFilters([]);
      setPagination({ total: 0, pages: 1 });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const params = {
          page,
          limit: itemsPerPage,
          sort: sortBy
        };
        if (selectedBrand) params.brand = selectedBrand;
        if (priceRange?.min) params.min_price = priceRange.min;
        if (priceRange?.max) params.max_price = priceRange.max;
        if (stockFilter === 'in-stock') params.in_stock = 'true';
        if (stockFilter === 'out-of-stock') params.in_stock = 'false';

        const res = await api.searchPublicProducts(query, params);
        if (!cancelled) {
          setProducts(Array.isArray(res?.data) ? res.data : []);
          setBrandFilters(Array.isArray(res?.filters?.brands) ? res.filters.brands : []);
          setPagination(res?.pagination || { total: 0, pages: 1 });
        }
      } catch (e) {
        if (!cancelled) {
          setProducts([]);
          setError(e.message || 'Search failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [query, page, itemsPerPage, sortBy, selectedBrand, priceRange, stockFilter]);

  const featuredBrands = useMemo(() => {
    const source = brandFilters.length
      ? brandFilters
      : Array.from(new Set(products.map((p) => p.brand_name).filter(Boolean))).map((name) => ({ name }));
    if (source.length === 0) return [{ name: 'Honeywell', logo: honeywellLogo }];
    return source.slice(0, 6).map((b) => ({
      name: b.name,
      logo: b.name?.toLowerCase() === 'honeywell'
        ? honeywellLogo
        : `https://via.placeholder.com/120x36?text=${encodeURIComponent(b.name)}`
    }));
  }, [brandFilters, products]);

  const productCards = products.map(mapApiProductToCard);

  const clearFilters = () => {
    setSelectedBrand('');
    setSelectedPrice('');
    setStockFilter('');
  };

  const rangeStart = pagination.total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(page * itemsPerPage, pagination.total);

  return (
    <ShopLayout breadcrumbs={[{ label: query ? `Search: ${query}` : 'Search' }]}>
      <Seo
        title={query ? `Search: ${query} | AIDC Products | Chesi` : PAGE_SEO.search.title}
        description={
          query
            ? truncate(`Search results for "${query}" — barcode scanners, mobile computers, RFID and AIDC products from Chesi Kenya.`)
            : PAGE_SEO.search.description
        }
        path="/shop/search"
        noindex={Boolean(query)}
      />
      <div className="category-page search-results-page">
        {!query && recentSearches.length > 0 && (
          <div className="container search-page__recent-wrap">
            <div className="search-page__recent">
              <h2>Recent Searches</h2>
              <ul>
                {recentSearches.map((term) => (
                  <li key={term}>
                    <Link to={`/shop/search?q=${encodeURIComponent(term)}`}>{term}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {query && (
          <>
            <section className="category-header-strip search-results-header">
              <div className="container">
                <div className="breadcrumbs">
                  <Link to="/shop">Shop</Link>
                  <span>›</span>
                  <span>Search</span>
                </div>
                <div className="header-main">
                  <div>
                    <h1>Search results for &quot;{query}&quot;</h1>
                    {pagination.total > 0 && (
                      <p className="search-results-summary">
                        Showing {rangeStart} – {rangeEnd} of {pagination.total} results for <strong>&apos;{query}&apos;</strong>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="category-main-content">
              <div className="container">
                <div className="content-layout">
                  <aside className="filter-sidebar">
                    <div className="sidebar-card">
                      <h3>Shop By</h3>
                      {brandFilters.length > 0 && (
                        <details className="filter-group" open>
                          <summary>Brand</summary>
                          <div className="filter-options">
                            {brandFilters.map((b) => (
                              <label key={b.id || b.slug}>
                                <input
                                  type="checkbox"
                                  checked={selectedBrand === b.slug}
                                  onChange={() => setSelectedBrand(selectedBrand === b.slug ? '' : b.slug)}
                                />
                                {b.name}
                                {b.product_count != null && ` (${b.product_count})`}
                              </label>
                            ))}
                          </div>
                        </details>
                      )}
                      <details className="filter-group" open>
                        <summary>Price</summary>
                        <div className="filter-options">
                          {PRICE_RANGES.map((r) => (
                            <label key={r.label}>
                              <input
                                type="radio"
                                name="price"
                                checked={selectedPrice === r.label}
                                onChange={() => setSelectedPrice(selectedPrice === r.label ? '' : r.label)}
                              />
                              {r.label}
                            </label>
                          ))}
                        </div>
                      </details>
                      <details className="filter-group" open>
                        <summary>Availability</summary>
                        <div className="filter-options">
                          <label>
                            <input type="radio" name="stock" checked={stockFilter === ''} onChange={() => setStockFilter('')} />
                            All
                          </label>
                          <label>
                            <input type="radio" name="stock" checked={stockFilter === 'in-stock'} onChange={() => setStockFilter('in-stock')} />
                            In Stock
                          </label>
                          <label>
                            <input type="radio" name="stock" checked={stockFilter === 'out-of-stock'} onChange={() => setStockFilter('out-of-stock')} />
                            Out of Stock
                          </label>
                        </div>
                      </details>
                      {(selectedBrand || selectedPrice || stockFilter) && (
                        <button type="button" className="filter-clear" onClick={clearFilters}>
                          Clear filters
                        </button>
                      )}
                    </div>
                  </aside>

                  <div className="main-content-area">
                    <section className="brand-strip">
                      <span>Top brands</span>
                      <div className="brand-strip-logos">
                        {featuredBrands.map((brand) => (
                          <img key={brand.name} src={brand.logo} alt={brand.name} />
                        ))}
                      </div>
                    </section>

                    <div className="product-controls">
                      <div className="control-left">
                        <label>
                          Show:
                          <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                            <option value={12}>12</option>
                            <option value={20}>20</option>
                            <option value={40}>40</option>
                          </select>
                        </label>
                        <span className="results-count">{pagination.total} products</span>
                      </div>
                      <div className="control-center">
                        <label>
                          Sort by:
                          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                            <option value="featured">Best Match</option>
                            <option value="newest">Newest</option>
                            <option value="best-selling">Best Selling</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="price-high">Price: High to Low</option>
                            <option value="name">Name A-Z</option>
                          </select>
                        </label>
                      </div>
                      <div className="control-right">
                        <span>View:</span>
                        <button type="button" className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')}>List</button>
                        <button type="button" className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')}>Grid</button>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center p-4">Searching…</div>
                    ) : error ? (
                      <div className="alert alert-warning mb-0">{error}</div>
                    ) : (
                      <div className={`products-list ${viewMode === 'grid' ? 'products-grid-view' : 'products-list-view'}`}>
                        {productCards.map((product) => (
                          viewMode === 'grid' ? (
                            <ProductCard key={product.id} product={product} />
                          ) : (
                            <CategoryProductRow
                              key={product.id}
                              product={product}
                              categoryTitle={product.categoryName || 'Search'}
                            />
                          )
                        ))}
                        {productCards.length === 0 && (
                          <div className="text-center p-4">No products matched your search. Try a different keyword or SKU.</div>
                        )}
                      </div>
                    )}

                    <Pagination page={page} pages={pagination.pages} onPageChange={setPage} />
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {!query && (
          <div className="container search-page__empty-prompt">
            <h1>Search Products</h1>
            <p>Type at least three characters in the search bar to see suggestions, or press Enter to view full results.</p>
          </div>
        )}
      </div>
    </ShopLayout>
  );
}

export default SearchPage;
